import React, { useState, useRef, useEffect } from "react";
import ProgressBar from "../ProgressBar";
import LessonUI from "../ScienceLessonUI";
import { generateAdvancedScienceQuiz } from "../AdvanceScienceQuizPool";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg"
import { ScienceStoryModeQuiz } from "../ScienceStoryModeStructure";
import { awardToken } from "../../awardtoken.js";

// Pages array (no change needed)
export const Grade12PhysicsLessonPages = [
    {
      title: "Introduction to Grade 12 Physics",
      content: (
        <>
          <p>
            Welcome to Grade 12 Physics! This year, you’ll tackle advanced motion, forces, energy, waves, fields, and the mysteries of the quantum universe. These concepts shape the technologies, engineering, and discoveries that are changing our world.
          </p>
          <ul>
            <li><b>Advanced Kinematics & Dynamics:</b> Motion and forces in two and three dimensions</li>
            <li><b>Energy, Momentum, and Collisions:</b> Conservation laws in complex systems, from sports to car crashes</li>
            <li><b>Waves, Light & Modern Physics:</b> How energy moves, dual nature of light, and applications in imaging and communication</li>
            <li><b>Electricity & Magnetism:</b> Fields, circuits, motors, generators, and the power grid</li>
            <li><b>Quantum Physics & Relativity:</b> The most mind-bending theories driving new tech, from GPS to quantum computers</li>
          </ul>
          <p>
            <b>Big Question:</b> How does understanding advanced physics help us solve 21st-century problems and imagine new futures?
          </p>
          <p>
            <i>Career Lens:</i> Physics opens doors to engineering, space science, medical tech, AI, climate solutions, and beyond.
          </p>
        </>
      ),
    },
    {
      title: "Chapter 1: Advanced Kinematics & Dynamics",
      topic: "Advanced Mechanics",
      content: (
        <>
          <h4>Beyond the Basics of Motion</h4>
          <ul>
            <li>2D and 3D motion: projectiles, satellites, circular and rotational motion</li>
            <li>Real-world forces: friction, tension, normal, centripetal, and gravity in complex systems</li>
            <li>Nonlinear dynamics: banked curves, looping roller coasters, and orbits</li>
            <li>Newton’s laws in engineering: bridge design, sports science, safety systems</li>
          </ul>
          <p>
            <b>Try this:</b> Use a phone or simulation to model the path of a projectile or a satellite in orbit. How do launch speed and angle affect the outcome?
          </p>
          <p>
            <i>Next-gen physics:</i> How do physicists and engineers use advanced dynamics to design drones, Mars rovers, or amusement park rides?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 2: Energy, Momentum, and Collisions",
      topic: "Energy, Momentum & Collisions",
      content: (
        <>
          <h4>Keeping Track of Energy & Motion</h4>
          <ul>
            <li>Work, energy, and power: solving multi-step problems with kinetic, potential, and mechanical energy</li>
            <li>Impulse and conservation of momentum: car safety, sports, explosions, and astrophysics</li>
            <li>Elastic vs. inelastic collisions: 1D, 2D, and “real-life” cases</li>
            <li>Complex systems: analyzing multi-object interactions (e.g., pool balls, traffic accidents, rocket launches)</li>
          </ul>
          <p>
            <b>Lab:</b> Use video analysis or motion sensors to study a collision (or watch a YouTube slow-motion experiment). Are energy and momentum always conserved?
          </p>
          <p>
            <i>Societal Impact:</i> How has our understanding of collisions led to better car design, sports gear, and safety equipment?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 3: Waves, Light & Modern Physics",
      topic: "Waves & Modern Physics",
      content: (
        <>
          <h4>The Physics of Sound and Light</h4>
          <ul>
            <li>Wave phenomena: interference, diffraction, resonance, and the Doppler effect</li>
            <li>Light as both wave and particle: polarization, refraction, dispersion, and photoelectric effect</li>
            <li>Modern technology: fiber optics, lasers, X-rays, and medical imaging</li>
            <li>Unsolved mysteries: quantum weirdness and the limits of our current understanding</li>
          </ul>
          <p>
            <b>Explore:</b> Try an online simulation of the double-slit experiment or create rainbow patterns with a CD. How do physicists use light to “see” the invisible?
          </p>
          <p>
            <i>Future Lens:</i> Where will quantum optics and photonics take us—quantum internet, super-resolution microscopes, or even invisibility cloaks?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 4: Electricity & Magnetism",
      topic: "Electricity, Magnetism & EM Waves",
      content: (
        <>
          <h4>Forces of the Electromagnetic World</h4>
          <ul>
            <li>Electric fields, voltage, and potential: what’s really happening in a circuit?</li>
            <li>Capacitors, RC circuits, and their roles in technology</li>
            <li>Magnetic fields, electromagnetic induction, and Faraday’s Law: the foundation of our energy grid</li>
            <li>Applications: motors, generators, transformers, maglev trains, and wireless charging</li>
          </ul>
          <p>
            <b>Try this:</b> Build or simulate a simple circuit—how do voltage, resistance, and current interact? What happens if you move a magnet near a coil?
          </p>
          <p>
            <i>Frontiers:</i> How will advances in electromagnetism power next-gen tech, from renewable energy to electric vehicles and maglev transport?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 5: Quantum Physics & Relativity",
      topic: "Quantum Physics & Relativity",
      content: (
        <>
          <h4>Physics at the Edge of Reality</h4>
          <ul>
            <li>Quantum theory: uncertainty, quantization, probability, and the “strangeness” of the atomic world</li>
            <li>Special relativity: time dilation, length contraction, and the energy-mass connection (E=mc²)</li>
            <li>Modern applications: quantum computers, MRIs, GPS, and particle accelerators</li>
            <li>Exploring the unknown: black holes, gravitational waves, quantum entanglement</li>
          </ul>
          <p>
            <b>Thought Experiment:</b> If you traveled near the speed of light, how would your perception of time and space change?
          </p>
          <p>
            <i>Looking Ahead:</i> What breakthroughs might quantum physics and relativity bring in medicine, communications, or exploring the cosmos?
          </p>
        </>
      ),
    },
    {
      title: "Final Review & Physics in the Real World",
      isFinalReview: true,
      content: (
        <>
          <p>
            Physics powers tomorrow’s world—from artificial intelligence and quantum computers to clean energy and the search for new planets. You’re ready to tackle real problems, invent, and imagine what comes next!
          </p>
          <ul>
            <li>Review all chapters, quizzes, and problem-solving techniques</li>
            <li>Investigate a physics breakthrough: gravitational waves, materials science, fusion energy, or quantum tech</li>
            <li>Reflect: How does a physics mindset help you adapt, innovate, and thrive in a rapidly changing world?</li>
          </ul>
          <p>
            <b>Next Steps:</b> Keep exploring, building, and asking questions—the universe is your lab!
          </p>
        </>
      ),
    },
  ];
  

export default function Grade12PhysicsLesson({ onAwardTokens }) {
    const [current, setCurrent] = useState(0);
    const [showQuiz, setShowQuiz] = useState(null); // null, "chapter", or "final"
    const [quizResult, setQuizResult] = useState(null);
    const [user, setUser] = useState(null);
    const hasAwardedToken = useRef(false);
  
    // Track chapters passed
    const [chaptersPassed, setChaptersPassed] = useState(() =>
      JSON.parse(localStorage.getItem("phys12ChaptersPassed") || "{}")
    );
  
    useEffect(() => {
      const logged = JSON.parse(localStorage.getItem("loggedInUser"));
      if (logged) setUser(logged);
    }, []);
  
    // CHAPTER quiz logic
    const currentTopic = Grade12PhysicsLessonPages[current]?.topic;
    const isFinalReview = Grade12PhysicsLessonPages[current]?.isFinalReview;
    let quizQuestions = [];
    if (showQuiz === "chapter" && currentTopic) {
      quizQuestions = generateAdvancedScienceQuiz({
        subject: "Physics",
        grade: 12,
        topic: currentTopic,
        count: 5,
      });
    } else if (showQuiz === "final") {
      const topics = [
        "Advanced Mechanics",
        "Energy, Momentum & Collisions",
        "Waves & Modern Physics",
        "Electricity, Magnetism & EM Waves",
        "Quantum Physics & Relativity"
      ];
      let pool = [];
      topics.forEach(topic => {
        pool = pool.concat(
          generateAdvancedScienceQuiz({
            subject: "Physics",
            grade: 12,
            topic,
            count: 10
          })
        );
      });
      quizQuestions = pool.sort(() => Math.random() - 0.5).slice(0, 15);
    }
  
    useEffect(() => {
      if (quizResult && !hasAwardedToken.current) {
        const percent = (quizResult.score / quizResult.total) * 100;
        if (percent >= 80) {
          if (typeof onAwardTokens === "function") {
            onAwardTokens(1);
          } else {
            awardToken();
          }
          hasAwardedToken.current = true;
        }
      }
    }, [quizResult, onAwardTokens]);
  
    // Gated navigation
    const handleNextPage = () => {
      const nextIndex = current + 1;
      const nextPage = Grade12PhysicsLessonPages[nextIndex];
  
      if (
        nextPage &&
        nextPage.topic !== currentTopic &&
        currentTopic && // Only gate if topic exists!
        !chaptersPassed[currentTopic]
      )
       {
        setShowQuiz("chapter");
        setQuizResult(null);
        return;
      }
      setCurrent(nextIndex);
    };
  
    // Quiz UI (marks chapter as passed if successful)
    if (showQuiz && quizQuestions.length > 0) {
      return (
        <ScienceStoryModeQuiz
          questions={quizQuestions}
          onFinish={(score, total) => {
  setShowQuiz(null);
  setQuizResult({ score, total });
  hasAwardedToken.current = false;
  const percent = total > 0 ? Math.round((score / total) * 100) : 0;

  // --- Save quiz result for Student Dashboard ---
  if (showQuiz === "chapter" && currentTopic) {
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults[`physics_g12_${currentTopic}`] = {
      score,
      total,
      percent,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
  }
  if (showQuiz === "final") {
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["physics_g12_final"] = {
      score,
      total,
      percent,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
  }
  // ---------------------------------------------

  if (percent >= 80 && currentTopic && showQuiz === "chapter") {
    setChaptersPassed(prev => {
      const updated = { ...prev, [currentTopic]: true };
      localStorage.setItem("phys12ChaptersPassed", JSON.stringify(updated));
      return updated;
    });
  }
}}
          studentAvatar={user?.avatar ?? defaultProfile}
        />
      );
    }
  
    // Quiz result/congrats screen
    if (quizResult) {
      const percent = Math.round((quizResult.score / quizResult.total) * 100);
      const passed = percent >= 80;
      return (
        <div className="quiz-result-container" style={{ textAlign: "center", marginTop: 48 }}>
          <h2 style={{ color: "#d4145a" }}>Quiz Complete!</h2>
          <p style={{ fontSize: 22, margin: "18px 0" }}>
            Your Score: {quizResult.score} / {quizResult.total} ({percent}%)
          </p>
          {passed ? (
            <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
              🎉 Congratulations! You earned a Science token!
            </div>
          ) : (
            <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
              Try again to earn a Science token (80% required).
            </div>
          )}
          {passed ? (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => {
                setQuizResult(null);
                setCurrent(c => c + 1);
              }}
            >
              Next Chapter
            </button>
          ) : (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => setQuizResult(null)}
            >
              Return to Lesson
            </button>
          )}
        </div>
      );
    }
  
    // Main lesson rendering
    return (
      <div className="lesson-page">
        <ProgressBar progress={Math.round((current / (Grade12PhysicsLessonPages.length - 1)) * 100)} />
        <LessonUI
          pages={[Grade12PhysicsLessonPages[current]]}
          currentPageIndex={0}
          onPrevPage={current > 0 ? () => setCurrent(c => c - 1) : undefined}
          onNextPage={current < Grade12PhysicsLessonPages.length - 1 ? handleNextPage : undefined}
          backTo="/science"
        />
        {/* Chapter quiz button */}
        {currentTopic && !isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("chapter");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Start Quiz for This Chapter
            </button>
          </div>
        )}
        {/* Final quiz button */}
        {isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("final");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Take Final Quiz for Grade 12 Physics
            </button>
          </div>
        )}
      </div>
    );
  }
  
